'use client'

/**
 * Generic full-screen image viewer. Controlled — caller owns `open`
 * state. Supports a single image or a gallery with prev/next.
 *
 * - Esc to close
 * - ← / → to navigate
 * - Click outside the image to close
 * - Image rendered at object-contain so the full image is visible without
 *   crop, regardless of original aspect ratio.
 *
 * Use this anywhere the user should be able to "tap to see the
 * full-size version" of an image — flogger blueprints, art galleries,
 * scene panels, etc. Mirror the pattern from `submission-gallery.tsx`
 * which had this inlined; that file should migrate to this component
 * the next time it's touched.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'

export interface LightboxImage {
  src: string
  alt?: string
  caption?: string
}

interface LightboxProps {
  images: LightboxImage[]
  open: boolean
  onClose: () => void
  initialIndex?: number
  /** When false, prev/next stop at the first/last image instead of
   *  wrapping around. Defaults to true (wrap) so existing callers
   *  keep their current behavior. */
  loop?: boolean
}

export function Lightbox({
  images,
  open,
  onClose,
  initialIndex = 0,
  loop = true,
}: LightboxProps) {
  if (!open || images.length === 0) return null
  return (
    <LightboxOverlay
      images={images}
      onClose={onClose}
      initialIndex={Math.max(0, Math.min(initialIndex, images.length - 1))}
      loop={loop}
    />
  )
}

/**
 * Internal — only mounts while open, so the caller's `initialIndex`
 * is honored on every open without an effect-driven re-sync (which
 * triggers the React 19 cascading-render warning).
 */
function LightboxOverlay({
  images,
  onClose,
  initialIndex,
  loop,
}: {
  images: LightboxImage[]
  onClose: () => void
  initialIndex: number
  loop: boolean
}) {
  const [index, setIndex] = useState(initialIndex)
  // Render into document.body so the fullscreen overlay escapes any parent
  // stacking context (e.g. a `position: sticky` media pane) that would
  // otherwise let the z-50 site header paint over the z-80 lightbox.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  // Step the index by ±1. When `loop` is on it wraps; when off it
  // clamps at the first / last image.
  const step = (i: number, delta: number): number => {
    const n = images.length
    if (loop) return (i + delta + n) % n
    return Math.max(0, Math.min(n - 1, i + delta))
  }

  // Esc closes; arrows navigate when there's more than one image.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (images.length > 1) {
        if (e.key === 'ArrowLeft') {
          setIndex((i) => step(i, -1))
        } else if (e.key === 'ArrowRight') {
          setIndex((i) => step(i, 1))
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // `step` closes over `loop` + `images.length`, both stable per mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length, onClose, loop])

  const current = images[Math.max(0, Math.min(index, images.length - 1))]
  const atStart = !loop && index <= 0
  const atEnd = !loop && index >= images.length - 1
  const prev = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIndex((i) => step(i, -1))
  }
  const next = (e: React.MouseEvent) => {
    e.stopPropagation()
    setIndex((i) => step(i, 1))
  }

  const overlay = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={current.alt || 'Image viewer'}
      // Pin the image to the top on every viewport (justify-start +
      // pt-16 to clear the close button). Vertical centering causes
      // the image to shift up or down between images with different
      // caption lengths — visible on desktop too, not just mobile.
      className="fixed inset-0 z-[80] flex flex-col items-center justify-start bg-black/90 p-4 pt-16"
      onClick={onClose}
    >
      <button
        onClick={(e) => {
          e.stopPropagation()
          onClose()
        }}
        className="absolute right-4 top-4 bg-black/40 p-2 text-white transition-colors hover:bg-black/60"
        aria-label="Close"
      >
        <X className="h-6 w-6" />
      </button>

      {images.length > 1 && (
        <>
          <button
            onClick={prev}
            disabled={atStart}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/40 p-2 text-white transition-colors hover:bg-black/60 disabled:cursor-default disabled:opacity-30 disabled:hover:bg-black/40"
            aria-label="Previous image"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={next}
            disabled={atEnd}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/40 p-2 text-white transition-colors hover:bg-black/60 disabled:cursor-default disabled:opacity-30 disabled:hover:bg-black/40"
            aria-label="Next image"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      <div
        // Column height accounts for the larger top padding
        // (pt-16 = 4rem, plus pb-4 = 1rem → 5rem of chrome). Same on
        // every viewport now that the image is top-pinned everywhere.
        className="flex max-h-[calc(100vh-5rem)] max-w-[calc(100vw-2rem)] flex-col items-center gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={current.src}
          alt={current.alt || ''}
          // Image height is capped at ~10% under the available column
          // height (90vh − chrome) and `shrink-0` keeps the flex column
          // from squeezing it further when a caption is long. The
          // freed-up ~10% becomes guaranteed caption headroom, so the
          // image position doesn't shift between images with different
          // caption lengths and long captions don't get pushed off the
          // bottom of the screen.
          className="max-h-[calc(90vh-10rem)] max-w-full shrink-0 object-contain"
        />
        {current.caption && (
          <p className="max-w-2xl whitespace-pre-line text-center text-sm text-white/80">
            {current.caption}
          </p>
        )}
        {images.length > 1 && (
          <p className="text-xs text-white/60">
            {index + 1} / {images.length}
          </p>
        )}
      </div>
    </div>
  )

  return mounted ? createPortal(overlay, document.body) : null
}
